import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useGoToPostAuthRoute } from '@/hooks/usePostAuthRoute';
import { translateApiError } from '@/lib/apiError';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { PasswordInput } from '@/components/synapse/PasswordInput';
import { PwStrength } from '@/components/synapse/PwStrength';
import { PolicyLine } from '@/components/synapse/PolicyLine';
import { AuthScreen } from '@/layouts/AuthScreen';
import { BrandPanel } from '@/components/synapse/BrandPanel';
import { passwordPolicy, passwordScore } from '@/lib/passwordScore';

export function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const goToPostAuthRoute = useGoToPostAuthRoute();

  const [params] = useSearchParams();
  /**
   * Pre-filled from the invitation email's link.
   *
   * More than a convenience: invitations are matched to an account BY EMAIL, so
   * somebody who arrives from an invite and signs up with a different address —
   * the personal one they happened to read the mail in — lands in an account
   * that will never see the invitation and offers no clue why. Pre-filling makes
   * the address that works the one already in the box.
   *
   * Read once as the initial state rather than synced to the URL, so a user who
   * corrects it is not fighting the query string on every render. It stays fully
   * editable, and the server re-derives everything from what is submitted — a
   * tampered value buys nothing but a filled-in text input.
   */
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(() => params.get('email')?.trim() ?? '');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const score = useMemo(() => passwordScore(password), [password]);
  const policy = useMemo(() => passwordPolicy(password), [password]);
  const passwordTooShort = password.length > 0 && password.length < 8;

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    password.length >= 8 &&
    acceptedTerms &&
    !submitting;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const session = await register({
        email: email.trim(),
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        password,
      });
      goToPostAuthRoute(session.memberships);
    } catch (err) {
      setError(translateApiError(err, { fallback: t('auth.createAccountFailed') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthScreen
      brand={
        <BrandPanel
          heading={
            <>
              {t('auth.brand.registerHeadingLine1')}
              <br />
              {t('auth.brand.registerHeadingLine2')}
            </>
          }
          body={t('auth.brand.registerBody')}
          features={[
            {
              icon: 'box',
              title: t('auth.brand.featureTrackTitle'),
              body: t('auth.brand.featureTrackBody'),
            },
            {
              icon: 'users',
              title: t('auth.brand.featureRolesTitle'),
              body: t('auth.brand.featureRolesBody'),
            },
            {
              icon: 'eye',
              title: t('auth.brand.featureCalmTitle'),
              body: t('auth.brand.featureCalmBody'),
            },
          ]}
          footer={
            <div className="flex w-full justify-between">
              <span>
                {t('auth.alreadyHaveAccount')}{' '}
                <Link to="/login" className="text-ink underline underline-offset-[3px]">
                  {t('auth.signIn')}
                </Link>
              </span>
              <span className="mono">{t('common.version')}</span>
            </div>
          }
        />
      }
    >
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-2xl font-semibold leading-[1.1] tracking-[-0.025em] md:text-[22px]">
            {t('auth.createYourAccount')}
          </h1>
          <p className="mt-2 text-[13.5px] leading-[1.5] text-muted md:text-[13px]">
            {t('auth.oneLoginCovers')}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
          <div className="grid grid-cols-2 gap-2.5">
            <Field label={t('common.firstName')}>
              <Input
                inputSize="lg"
                required
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field label={t('common.lastName')}>
              <Input
                inputSize="lg"
                required
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
          </div>

          <Field label={t('common.workEmail')} hint={t('auth.useStaffCredentials')}>
            <Input
              type="email"
              inputSize="lg"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field
            label={t('common.password')}
            error={passwordTooShort ? t('auth.atLeast8Error') : undefined}
          >
            {(id) => (
              <div>
                <PasswordInput
                  id={id}
                  required
                  inputSize="lg"
                  autoComplete="new-password"
                  placeholder={t('auth.atLeast8')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {password.length > 0 && (
                  <div className="mt-2 flex flex-col gap-2">
                    <PwStrength score={score} />
                    <div className="flex flex-col gap-1">
                      {policy.map((p) => (
                        <PolicyLine key={p.labelKey} ok={p.ok} label={t(p.labelKey)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Field>

          <label className="flex items-start gap-2 text-[12.5px] leading-[1.5] text-ink-2">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 accent-ink"
            />
            <span>
              <Trans
                i18nKey="auth.agreeTermsLine"
                components={{
                  terms: <a className="text-ink underline underline-offset-[3px]" href="#" />,
                  policy: <a className="text-ink underline underline-offset-[3px]" href="#" />,
                }}
              />
            </span>
          </label>

          {error && (
            <div className="text-[12.5px] text-destructive" role="alert">
              {error}
            </div>
          )}

          <Button type="submit" size="lg" full disabled={!canSubmit}>
            {submitting ? t('auth.creatingAccount') : t('auth.createAccount')}
          </Button>
        </form>

        <div className="text-center text-[13px] text-muted">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login" className="text-ink underline underline-offset-[3px]">
            {t('auth.signIn')}
          </Link>
        </div>
      </div>
    </AuthScreen>
  );
}
